import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

const eventTypeLabels = {
    program: 'Program',
    commercial: 'Reklame',
    promo: 'Promo',
    ident: 'Ident',
    news: 'Nyheter',
    live: 'Live',
    break: 'Pause',
    filler: 'Filler',
};

const timingTypeLabels = {
    hard: 'Fast tid',
    soft: 'Fleksibel',
    flexible: 'Fleksibel',
    float: 'Flytende',
};

const sourceTypeLabels = {
    file: 'Fil',
    live: 'Live',
    feed: 'Feed',
    manual: 'Manuell',
    placeholder: 'Plassholder',
};

@observer
class EventView extends Component {
    loadAll = async () => {
        const { id } = this.props;
        const { broadcastEventStore } = this.props.stores;
        if (id) {
            const response = await util.fetchApi('/api/broadcastevents/', { method: 'GET' }, { id });
            if (response.status === 200 && response.data?.length > 0) {
                broadcastEventStore.updateKeyValue('broadcastEvent', response.data[0]);
            }
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    formatDateTime(dateStr) {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleString('nb-NO', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    formatDuration(seconds) {
        if (!seconds) return '-';
        const mins = Math.floor(seconds / 60);
        if (mins < 60) return `${mins} min`;
        const hours = Math.floor(mins / 60);
        const remMins = mins % 60;
        return remMins > 0 ? `${hours}t ${remMins}m` : `${hours}t`;
    }

    closeDrawer = () => {
        const { callback = () => {}, stores, drawerLevel = 1 } = this.props;
        const { appState } = stores;
        appState.toggleDrawer(false, drawerLevel);
        callback();
    }

    render() {
        const { drawerLevel } = this.props;
        const { userStore, broadcastEventStore } = this.props.stores;
        const { user } = userStore;
        const { broadcastEvent: event } = broadcastEventStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (!event || !event.id) {
            return (
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100 p-4 text-center text-muted'>
                        Laster...
                    </div>
                </div>
            );
        }

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <h5 class='ms-3 mb-3'>
                        <i class='fa-solid fa-calendar me-2' />
                        {event.title || 'Hendelse'}
                    </h5>

                    <div class={`mx-3 p-3 rounded ${darkmode ? 'bg-secondary' : 'bg-light'}`}>
                        {/* Title and subtitle */}
                        <div class='mb-3'>
                            <h4 class='mb-1'>{event.title || 'Uten tittel'}</h4>
                            {event.subtitle && <p class='text-muted mb-0'>{event.subtitle}</p>}
                        </div>

                        {/* Time info */}
                        <div class='row mb-3'>
                            <div class='col-6'>
                                <small class='text-muted d-block'>Planlagt start</small>
                                <strong>{this.formatDateTime(event.scheduledStart)}</strong>
                            </div>
                            <div class='col-6'>
                                <small class='text-muted d-block'>Planlagt slutt</small>
                                <strong>{this.formatDateTime(event.scheduledEnd)}</strong>
                            </div>
                        </div>

                        {event.durationSeconds && (
                            <div class='mb-3'>
                                <small class='text-muted d-block'>Varighet</small>
                                <strong>{this.formatDuration(event.durationSeconds)}</strong>
                            </div>
                        )}

                        {/* Type info */}
                        <div class='row mb-3'>
                            <div class='col-4'>
                                <small class='text-muted d-block'>Type</small>
                                <strong>{eventTypeLabels[event.eventType] || event.eventType || '-'}</strong>
                            </div>
                            <div class='col-4'>
                                <small class='text-muted d-block'>Timing</small>
                                <strong>{timingTypeLabels[event.timingType] || event.timingType || '-'}</strong>
                            </div>
                            <div class='col-4'>
                                <small class='text-muted d-block'>Kilde</small>
                                <strong>{sourceTypeLabels[event.sourceType] || event.sourceType || '-'}</strong>
                            </div>
                        </div>

                        {/* Asset info */}
                        {event.assetId && (
                            <div class='mb-3'>
                                <small class='text-muted d-block'>Asset ID</small>
                                <strong>{event.assetId}</strong>
                            </div>
                        )}

                        {event.assetPath && (
                            <div class='mb-3'>
                                <small class='text-muted d-block'>Asset Path</small>
                                <code class='small'>{event.assetPath}</code>
                            </div>
                        )}

                        {/* Notes */}
                        {event.operationalNotes && (
                            <div class='mb-3'>
                                <small class='text-muted d-block'>Operative notater</small>
                                <p class='mb-0'>{event.operationalNotes}</p>
                            </div>
                        )}

                        {event.producerNotes && (
                            <div class='mb-3'>
                                <small class='text-muted d-block'>Produsent notater</small>
                                <p class='mb-0'>{event.producerNotes}</p>
                            </div>
                        )}

                        {/* Compliance */}
                        {(event.ageRating || event.contentWarnings?.length > 0) && (
                            <div class='row mb-3'>
                                {event.ageRating && (
                                    <div class='col-6'>
                                        <small class='text-muted d-block'>Aldersgrense</small>
                                        <strong>{event.ageRating}</strong>
                                    </div>
                                )}
                                {event.contentWarnings?.length > 0 && (
                                    <div class='col-6'>
                                        <small class='text-muted d-block'>Innholdsadvarsler</small>
                                        <strong>{event.contentWarnings.join(', ')}</strong>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Close button */}
                    <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                        <button type='button' class='btn btn-secondary rounded-pill' onClick={this.closeDrawer}>
                            <i class='fa-solid fa-times me-2' />
                            Lukk
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default EventView;
